/**
 * LocationTagsSection Component
 * Displays location-level tag suggestions and confirmed tags.
 * A simplified version that only handles location tags (no image grids).
 */

import { useState, useCallback } from 'react';
import { AlertCircle, Clock, CheckCircle2, Loader2, CheckCheck, XCircle, Info, Sparkles, X, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  useTagSuggestions,
  useAcceptTags,
  useRejectTags,
  useAcceptAllTags,
  useRejectAllTags,
  useRemoveTag,
  useAvailableTags,
  useReanalyzeLocationText,
} from '@/api/tag-suggestions';
import type { TagAnalysisStatus } from '@/types/api';
import { formatAiRunUsage } from '@/lib/ai-run-usage';
import { AnalyzeTagsButton } from './AnalyzeTagsButton';
import { TagSuggestionCard } from './TagSuggestionCard';
import { TagSelector } from './TagSelector';

interface LocationTagsSectionProps {
  locationId: string;
  /** Callback when analysis is triggered */
  onAnalysisTriggered?: () => void;
  /** Callback when tags are changed (confirmed, rejected, or manually added/removed) */
  onTagsChange?: () => void;
}

/**
 * Status indicator for analysis state
 */
function StatusIndicator({ status }: { status: TagAnalysisStatus }): JSX.Element {
  switch (status) {
    case 'analyzing':
      return (
        <div className="flex items-center gap-2 text-blue-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Analyzing...</span>
        </div>
      );
    case 'completed':
      return (
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle2 className="h-4 w-4" />
          <span className="text-sm">Complete</span>
        </div>
      );
    case 'failed':
    case 'timeout':
      return (
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{status === 'timeout' ? 'Timed out' : 'Failed'}</span>
        </div>
      );
    case 'pending':
    default:
      return (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span className="text-sm">Not analyzed</span>
        </div>
      );
  }
}

/**
 * Loading skeleton
 */
function LoadingSkeleton(): JSX.Element {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-8 w-24" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-6 w-16" />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Removable tag badge (Phase 26)
 */
interface RemovableTagProps {
  tagId: number;
  tagName: string;
  isAiSuggested: boolean;
  isRemoving: boolean;
  onRemove: () => void;
}

function RemovableTag({
  tagId,
  tagName,
  isAiSuggested,
  isRemoving,
  onRemove,
}: RemovableTagProps): JSX.Element {
  // AI-confirmed tags are green, manual tags are blue
  const colorClasses = isAiSuggested
    ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
    : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100';

  return (
    <Badge
      variant="secondary"
      className={`inline-flex items-center gap-1 px-2 py-0.5 pr-1 group ${colorClasses} ${isRemoving ? 'opacity-50' : ''}`}
      data-testid={`tag-${String(tagId)}`}
    >
      {isAiSuggested && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Sparkles className="h-3 w-3 text-amber-500 flex-shrink-0" />
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            AI suggested
          </TooltipContent>
        </Tooltip>
      )}
      <span>{tagName}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={isRemoving}
        onClick={(e) => {
          e.preventDefault();
          onRemove();
        }}
        className="h-4 w-4 p-0 ml-0.5 opacity-60 hover:opacity-100 hover:bg-transparent"
        data-testid={`remove-tag-${String(tagId)}`}
      >
        {isRemoving ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <X className="h-3 w-3" />
        )}
      </Button>
    </Badge>
  );
}

/**
 * LocationTagsSection displays location-level tag suggestions and confirmed tags.
 * Image-level tags are handled by UnifiedImageGrid.
 */
export function LocationTagsSection({
  locationId,
  onAnalysisTriggered,
  onTagsChange,
}: LocationTagsSectionProps): JSX.Element {
  const { data, isLoading, error, refetch } = useTagSuggestions(locationId);
  const { data: availableTags } = useAvailableTags();
  const acceptMutation = useAcceptTags();
  const rejectMutation = useRejectTags();
  const acceptAllMutation = useAcceptAllTags();
  const rejectAllMutation = useRejectAllTags();
  const removeMutation = useRemoveTag();
  const reanalyzeLocationMutation = useReanalyzeLocationText();

  const [processingTags, setProcessingTags] = useState<Set<number>>(new Set());
  const [removingTags, setRemovingTags] = useState<Set<number>>(new Set());

  const startProcessing = useCallback((tagId: number) => {
    setProcessingTags((prev) => new Set(prev).add(tagId));
  }, []);

  const stopProcessing = useCallback((tagId: number) => {
    setProcessingTags((prev) => {
      const next = new Set(prev);
      next.delete(tagId);
      return next;
    });
  }, []);

  const handleAcceptTag = useCallback((tagId: number) => {
    startProcessing(tagId);
    acceptMutation.mutate(
      { locationId, tagIds: [tagId], scope: 'location' },
      {
        onSuccess: () => { onTagsChange?.(); },
        onSettled: () => { stopProcessing(tagId); },
      }
    );
  }, [locationId, acceptMutation, startProcessing, stopProcessing, onTagsChange]);

  const handleRejectTag = useCallback((tagId: number) => {
    startProcessing(tagId);
    rejectMutation.mutate(
      { locationId, tagIds: [tagId], scope: 'location' },
      {
        onSuccess: () => { onTagsChange?.(); },
        onSettled: () => { stopProcessing(tagId); },
      }
    );
  }, [locationId, rejectMutation, startProcessing, stopProcessing, onTagsChange]);

  const handleAcceptAll = useCallback(() => {
    acceptAllMutation.mutate(locationId, {
      onSuccess: () => { onTagsChange?.(); },
    });
  }, [locationId, acceptAllMutation, onTagsChange]);

  const handleRejectAll = useCallback(() => {
    rejectAllMutation.mutate(locationId, {
      onSuccess: () => { onTagsChange?.(); },
    });
  }, [locationId, rejectAllMutation, onTagsChange]);

  // Phase 26: Handle removing a tag (manual or AI-confirmed)
  const handleRemoveTag = useCallback((tagId: number) => {
    setRemovingTags((prev) => new Set(prev).add(tagId));
    removeMutation.mutate(
      { locationId, tagId, scope: 'location' },
      {
        onSuccess: () => { onTagsChange?.(); },
        onSettled: () => {
          setRemovingTags((prev) => {
            const next = new Set(prev);
            next.delete(tagId);
            return next;
          });
        },
      }
    );
  }, [locationId, removeMutation, onTagsChange]);

  // Handle re-analyze location text only
  const handleReanalyzeLocation = useCallback(() => {
    reanalyzeLocationMutation.mutate(locationId, {
      onSuccess: () => {
        void refetch();
        onAnalysisTriggered?.();
      },
    });
  }, [locationId, reanalyzeLocationMutation, refetch, onAnalysisTriggered]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Location Tags</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to load</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : 'Unknown error'}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const status = data?.status ?? 'pending';
  const suggestions = data?.suggestions ?? { location: [], images: {} };
  const locationTags = suggestions.location ?? [];
  const rejectedTags = data?.rejectedTags ?? [];
  const analysisError = data?.error;
  const serviceAvailable = data?.serviceAvailable ?? true;
  // The run that produced the location tags (FR-142); hidden while a new run replaces it
  const textRun = status === 'analyzing' ? null : (data?.runs?.location ?? null);

  // Phase 26: Get manual tags
  const manualLocationTagIds = data?.manualLocationTags ?? [];

  // Build a lookup map for tag names from available tags
  const tagNameMap = new Map<number, string>();
  (availableTags ?? []).forEach((tag) => {
    tagNameMap.set(tag.id, tag.name);
  });
  // Also add names from suggestions (in case availableTags hasn't loaded)
  locationTags.forEach((tag) => {
    tagNameMap.set(tag.tagId, tag.tagName);
  });

  // Get confirmed AI location tags
  const confirmedLocationTagIds = data?.confirmedLocationTags ?? [];
  const confirmedAiTags = confirmedLocationTagIds
    .map((tagId) => {
      const tagName = tagNameMap.get(tagId);
      return tagName ? { tagId, tagName, isAiSuggested: true } : null;
    })
    .filter((t): t is { tagId: number; tagName: string; isAiSuggested: boolean } => t !== null);

  // Get manual tags
  const manualTags = manualLocationTagIds
    .map((tagId) => {
      const tagName = tagNameMap.get(tagId);
      return tagName ? { tagId, tagName, isAiSuggested: false } : null;
    })
    .filter((t): t is { tagId: number; tagName: string; isAiSuggested: boolean } => t !== null);

  // Combine all confirmed tags (AI + manual)
  const allConfirmedTags = [...confirmedAiTags, ...manualTags];

  // Get pending location tags
  const pendingLocationTags = locationTags.filter(
    (tag) => !confirmedLocationTagIds.includes(tag.tagId) && !rejectedTags.includes(tag.tagId)
  );

  // Get all selected tag IDs (for excluding from TagSelector)
  // Include confirmed, manual, AND pending suggestion tag IDs to avoid duplicates
  const pendingTagIds = pendingLocationTags.map((t) => t.tagId);
  const allSelectedTagIds = [...confirmedLocationTagIds, ...manualLocationTagIds, ...pendingTagIds];

  const hasConfirmedTags = allConfirmedTags.length > 0;
  const hasPendingTags = pendingLocationTags.length > 0;
  const totalPending = pendingLocationTags.length;
  const isBulkProcessing = acceptAllMutation.isPending || rejectAllMutation.isPending;

  return (
    <TooltipProvider>
      <div className="space-y-3">
        {/* Service unavailable warning */}
        {!serviceAvailable && (
          <Alert variant="default" className="border-blue-200 bg-blue-50" data-testid="tag-service-unavailable-alert">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-800">AI Tag Service Temporarily Unavailable</AlertTitle>
            <AlertDescription className="text-blue-700">
              The AI tag analysis service is temporarily unavailable.
            </AlertDescription>
          </Alert>
        )}

        <Card data-testid="location-tags-section">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Location Tags</CardTitle>
                <CardDescription className="mt-0.5 text-xs">
                  AI-suggested tags for this location
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {/* Hide status indicator when analyzing - center content shows progress */}
                {status !== 'analyzing' && <StatusIndicator status={status} />}
                {/* Re-analyze Location button - only visible when we have existing tags */}
                {(status === 'completed' || status === 'failed') && (hasConfirmedTags || hasPendingTags) && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleReanalyzeLocation}
                        disabled={!serviceAvailable || reanalyzeLocationMutation.isPending}
                        className="h-8 px-2"
                        data-testid="reanalyze-location-btn"
                      >
                        {reanalyzeLocationMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Re-analyze location text</TooltipContent>
                  </Tooltip>
                )}
                <AnalyzeTagsButton
                  locationId={locationId}
                  status={status}
                  serviceAvailable={serviceAvailable}
                  onSuccess={() => {
                    void refetch();
                    onAnalysisTriggered?.();
                  }}
                />
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Error message */}
            {analysisError && (status === 'failed' || status === 'timeout') && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Analysis Error</AlertTitle>
                <AlertDescription>{analysisError}</AlertDescription>
              </Alert>
            )}

            {/* Pending state */}
            {status === 'pending' && (
              <p className="text-sm text-muted-foreground">
                Click "Analyze Tags" to generate AI-powered tag suggestions.
              </p>
            )}

            {/* Analyzing state */}
            {status === 'analyzing' && (
              <div className="flex flex-col items-center justify-center py-4 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mb-2" />
                <p className="text-sm">Analyzing location...</p>
              </div>
            )}

            {/* Confirmed tags (AI + manual) - always visible when there are confirmed tags */}
            {hasConfirmedTags && (
              <div data-testid="location-confirmed-tags">
                <h4 className="text-xs font-medium text-muted-foreground mb-2">Confirmed</h4>
                <div className="flex flex-wrap gap-1.5">
                  {allConfirmedTags.map((tag) => (
                    <RemovableTag
                      key={tag.tagId}
                      tagId={tag.tagId}
                      tagName={tag.tagName}
                      isAiSuggested={tag.isAiSuggested}
                      isRemoving={removingTags.has(tag.tagId)}
                      onRemove={() => { handleRemoveTag(tag.tagId); }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Pending tags with bulk actions - only when analysis completed and pending tags exist */}
            {(status === 'completed' || status === 'failed' || status === 'timeout') && hasPendingTags && (
              <div data-testid="location-pending-tags">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-medium text-muted-foreground">
                    Pending ({String(totalPending)})
                  </h4>
                  <div className="flex gap-1.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleAcceptAll}
                      disabled={isBulkProcessing}
                      className="h-6 px-2 text-xs"
                      data-testid="accept-all-location-tags-btn"
                    >
                      {acceptAllMutation.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : (
                        <CheckCheck className="h-3 w-3 mr-1" />
                      )}
                      Accept All
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRejectAll}
                      disabled={isBulkProcessing}
                      className="h-6 px-2 text-xs"
                      data-testid="reject-all-location-tags-btn"
                    >
                      {rejectAllMutation.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : (
                        <XCircle className="h-3 w-3 mr-1" />
                      )}
                      Reject All
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {pendingLocationTags.map((tag) => (
                    <TagSuggestionCard
                      key={tag.tagId}
                      tag={tag}
                      isProcessing={processingTags.has(tag.tagId)}
                      isRejected={rejectedTags.includes(tag.tagId)}
                      onAccept={() => { handleAcceptTag(tag.tagId); }}
                      onReject={() => { handleRejectTag(tag.tagId); }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* No tags message - only after analysis when no tags exist */}
            {!hasConfirmedTags && !hasPendingTags && status === 'completed' && (
              <p className="text-sm text-muted-foreground">
                No location-level tags suggested.
              </p>
            )}

            {/* What the listing-text run cost (FR-142) */}
            {textRun && (
              <p
                className="text-xs text-muted-foreground"
                title={`AI Router run ${textRun.runId}`}
                data-testid="location-tags-run-usage"
              >
                Model: {textRun.model} · {formatAiRunUsage(textRun)}
              </p>
            )}

            {/* Manual tag selector (Phase 26) - always visible */}
            <div data-testid="location-tag-selector-section">
              <h4 className="text-xs font-medium text-muted-foreground mb-2">Add Tag</h4>
              <TagSelector
                locationId={locationId}
                scope="location"
                excludeTagIds={allSelectedTagIds}
                placeholder="Select a tag to add..."
                testId="location-tag-selector"
                onTagAdded={onTagsChange}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}
